import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Zap, Clock, RefreshCw } from "lucide-react";
import dayjs from "dayjs";
import axios from "axios";
import { Card, CardHeader, CardTitle, CardExtra, CardContent } from "./components/ui/card";
import { Switch } from "./components/ui/switch";
import { Select } from "./components/ui/select";
import { Tooltip } from "./components/ui/tooltip";
import { Badge } from "./components/ui/badge";
import { DataTable } from "./components/ui/data-table";
import { Separator } from "./components/ui/separator";
import { toast } from "./lib/toast";
import apiUrl from "./config/config";

const token = "Bearer 0j2whpl3*s+2/j-hCl!!-5A-8Corubp5[gqzhGrA.b*hBdO[+r.yGIFpvpOqv";


const fmt2 = (n) =>
  (Number.isFinite(n) ? n : 0).toLocaleString("es-CL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const Statistic = ({ title, value, suffix, formatter }) => {
  const displayValue = formatter ? formatter(value) : value;
  return (
    <div>
      <p className="text-sm text-gray-500 mb-1">{title}</p>
      <p className="text-2xl font-bold text-gray-900">
        {displayValue}
        {suffix && <span className="text-base font-medium text-gray-500 ml-1">{suffix}</span>}
      </p>
    </div>
  );
};

export default function DashboardHome() {
  const [loadingPack, setLoadingPack] = useState(false);
  const [loadingRecep, setLoadingRecep] = useState(false);
  const [procesos, setProcesos] = useState([]);
  const [recepciones, setRecepciones] = useState([]);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [intervalSec, setIntervalSec] = useState(15);
  const [nextIn, setNextIn] = useState(15);

  const refreshTimerRef = useRef(null);
  const tickTimerRef = useRef(null);
  const prevProcesosRef = useRef([]);
  const [updatedKeys, setUpdatedKeys] = useState(new Set());
  const updatedClearTimerRef = useRef(null);

  const fetchPacking = useCallback(async () => {
    try {
      setLoadingPack(true);
      const res = await axios.get(
        `${apiUrl}/ServiceFamilyGrowers.asmx/FamilyGetResumenProcesos`,
        { headers: { Authorization: token, Accept: "application/json" } }
      );
      const payload = typeof res?.data === "string" ? JSON.parse(res.data) : res?.data;
      const list = Array.isArray(payload) ? payload : [];

      const prev = prevProcesosRef.current || [];
      const prevMap = new Map(
        prev.map((r) => [
          `${r?.Fecha}-${r?.NumOrdenPacking}`,
          { kE: Number(r?.kilosEntrada) || 0, kX: Number(r?.kilosexportados) || 0, est: r?.estado },
        ])
      );

      const changed = new Set();
      list.forEach((r) => {
        const key = `${r?.Fecha}-${r?.NumOrdenPacking}`;
        const before = prevMap.get(key);
        const now = {
          kE: Number(r?.kilosEntrada) || 0,
          kX: Number(r?.kilosexportados) || 0,
          est: r?.estado,
        };
        if (!before || before.kE !== now.kE || before.kX !== now.kX || String(before.est || "") !== String(now.est || "")) {
          changed.add(key);
        }
      });

      setProcesos(list);
      prevProcesosRef.current = list;
      setLastUpdate(new Date());
      setNextIn(intervalSec);

      if (changed.size) {
        setUpdatedKeys(changed);
        if (updatedClearTimerRef.current) clearTimeout(updatedClearTimerRef.current);
        updatedClearTimerRef.current = setTimeout(() => setUpdatedKeys(new Set()), 1500);
      }
    } catch (e) {
      console.error(e);
      toast.error("No se pudo cargar el resumen de procesos.");
    } finally {
      setLoadingPack(false);
    }
  }, [intervalSec]);

  const fetchRecepcion = useCallback(async () => {
    try {
      setLoadingRecep(true);
      const res = await axios.get(
        `${apiUrl}/ServiceFamilyGrowers.asmx/FamilyGetResumenRecepcion`,
        { headers: { Authorization: token, Accept: "application/json" } }
      );
      const payload = typeof res?.data === "string" ? JSON.parse(res.data) : res?.data;
      const list = Array.isArray(payload) ? payload : [];
      setRecepciones(list);
    } catch (e) {
      console.error(e);
      toast.error("No se pudo cargar el resumen de recepción.");
    } finally {
      setLoadingRecep(false);
    }
  }, []);

  useEffect(() => {
    fetchPacking();
    fetchRecepcion();
  }, [fetchPacking, fetchRecepcion]);

  useEffect(() => {
    if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
    if (tickTimerRef.current) clearInterval(tickTimerRef.current);

    if (autoRefresh) {
      refreshTimerRef.current = setInterval(fetchPacking, intervalSec * 1000);
      setNextIn(intervalSec);
      tickTimerRef.current = setInterval(() => {
        setNextIn((s) => (s > 1 ? s - 1 : intervalSec));
      }, 1000);
    }

    return () => {
      if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
      if (tickTimerRef.current) clearInterval(tickTimerRef.current);
    };
  }, [autoRefresh, intervalSec, fetchPacking]);

  const procesosHoy = useMemo(
    () => procesos.filter((r) => r?.Fecha && dayjs(r.Fecha).isSame(dayjs(), "day")),
    [procesos]
  );

  const recepcionesHoy = useMemo(
    () => recepciones.filter((r) => r?.FechaRecepcion && dayjs(r.FechaRecepcion).isSame(dayjs(), "day")),
    [recepciones]
  );

  const kpi = useMemo(() => {
    const totalEntrada = procesosHoy.reduce((acc, r) => acc + (Number(r?.kilosEntrada) || 0), 0);
    const totalExport = procesosHoy.reduce((acc, r) => acc + (Number(r?.kilosexportados) || 0), 0);
    const porcExport = totalEntrada > 0 ? Math.round((totalExport / totalEntrada) * 100) : 0;
    return { totalRecep: totalEntrada, totalExport, porcExport };
  }, [procesosHoy]);

  const columnasPacking = useMemo(() => [
    { title: "N° Proceso", dataIndex: "NumOrdenPacking", width: 110 },
    { title: "Productor", dataIndex: "Nombre", width: 180, render: (v) => v?.trim?.() ?? v },
    { title: "Variedad", dataIndex: "variedad", width: 150, render: (v) => v?.trim?.() ?? v },
    { title: "Kg Entrada", dataIndex: "kilosEntrada", align: "right", width: 130, render: (v) => fmt2(Number(v)), sorter: (a, b) => Number(a?.kilosEntrada || 0) - Number(b?.kilosEntrada || 0) },
    { title: "Kg Exportados", dataIndex: "kilosexportados", align: "right", width: 140, render: (v) => fmt2(Number(v)), sorter: (a, b) => Number(a?.kilosexportados || 0) - Number(b?.kilosexportados || 0) },
    {
      title: "% Exportación",
      key: "porcExport",
      align: "right",
      width: 130,
      render: (_, r) => {
        const entrada = Number(r?.kilosEntrada) || 0;
        const exp = Number(r?.kilosexportados) || 0;
        const pct = entrada > 0 ? (exp / entrada) * 100 : 0;
        return `${pct.toFixed(1)} %`;
      },
    },
    { title: "Fecha", dataIndex: "Fecha", width: 120, render: (v) => (v ? dayjs(v).format("YYYY-MM-DD") : "") },
  ], []);

  const columnasRecep = useMemo(() => [
    { title: "Fecha", dataIndex: "FechaRecepcion", width: 120, render: (v) => (v ? dayjs(v).format("YYYY-MM-DD") : "") },
    { title: "Productor", dataIndex: "productor", width: 200, render: (v) => v?.trim?.() ?? v },
    { title: "Variedad", dataIndex: "DescripcionVariedad", width: 140, render: (v) => v?.trim?.() ?? v },
    { title: "Kilos Aprobados", dataIndex: "KilosAprobados", align: "right", width: 140, render: (v) => fmt2(Number(v)) },
  ], []);

  const lastTimeStr = lastUpdate ? dayjs(lastUpdate).format("HH:mm:ss") : "—";

  return (
    <div className="p-0 space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent>
            <Statistic title="Total Recepción (Σ kilosEntrada)" value={kpi.totalRecep} formatter={(v) => fmt2(Number(v))} suffix="kg" />
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Statistic title="Total Exportación (Σ kilosexportados)" value={kpi.totalExport} formatter={(v) => fmt2(Number(v))} suffix="kg" />
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Statistic title="% Exportación (Total)" value={kpi.porcExport} suffix="%" />
          </CardContent>
        </Card>
      </div>

      {/* Órdenes de Packing */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap size={16} className="text-yellow-500" />
            Órdenes de Packing
          </CardTitle>
          <CardExtra>
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-green-500 animate-pulse" />
              <Badge variant="green">LIVE</Badge>
            </span>
            <Tooltip title="Última actualización">
              <span className="flex items-center gap-1 text-gray-400 text-xs">
                <Clock size={12} />
                {lastTimeStr}
              </span>
            </Tooltip>
            <span className="text-gray-300">•</span>
            <span className="text-xs text-gray-400">
              {autoRefresh ? `sig. en ${nextIn}s` : "auto OFF"}
            </span>
            <Separator orientation="vertical" className="h-4" />
            <Tooltip title={autoRefresh ? "Desactivar auto-actualización" : "Activar auto-actualización"}>
              <Switch checked={autoRefresh} onCheckedChange={setAutoRefresh} />
            </Tooltip>
            <Select
              style={{ width: 90 }}
              value={intervalSec}
              onChange={setIntervalSec}
              disabled={!autoRefresh}
              options={[10, 15, 30, 60].map((v) => ({ value: v, label: `${v}s` }))}
            />
            <Tooltip title="Actualizar ahora">
              <button
                type="button"
                onClick={fetchPacking}
                className="p-1 rounded hover:bg-gray-100 text-gray-500"
              >
                <RefreshCw size={14} className={loadingPack ? "animate-spin" : ""} />
              </button>
            </Tooltip>
          </CardExtra>
        </CardHeader>
        <CardContent className="pt-2">
          <DataTable
            columns={columnasPacking}
            data={procesosHoy}
            rowKey={(r) => `${r?.Fecha}-${r?.NumOrdenPacking}`}
            loading={loadingPack}
            scroll={{ x: 900 }}
            pagination={{ current: 1, pageSize: 10, showSizeChanger: false }}
            onPaginationChange={() => {}}
            rowClassName={(r) =>
              updatedKeys.has(`${r?.Fecha}-${r?.NumOrdenPacking}`)
                ? "bg-blue-50 transition-colors duration-1000"
                : ""
            }
          />
        </CardContent>
      </Card>

      {/* Recepción de Materia Prima */}
      <Card>
        <CardHeader>
          <CardTitle>Recepción de Materia Prima</CardTitle>
        </CardHeader>
        <CardContent className="pt-2">
          <DataTable
            columns={columnasRecep}
            data={recepcionesHoy}
            rowKey={(r) => `${r?.Fecha ?? ""}-${r?.Nombre ?? ""}-${r?.Variedad ?? ""}`}
            loading={loadingRecep}
            scroll={{ x: 700 }}
            pagination={{ current: 1, pageSize: 10, showSizeChanger: false }}
            onPaginationChange={() => {}}
          />
        </CardContent>
      </Card>
    </div>
  );
}
